import { FormEvent, useEffect, useState, useRef } from 'react';
import ButtonComponent from '../../components/ButtonComponent';
import InputField from '../../components/form/InputField';
import TextArea from '../../components/form/TextArea';
import useFormInput from '../../custom-hooks/useFormInput';
import {
  LuGlobe,
  LuImage,
  LuUser2,
  LuUsers,
  LuChurch,
  LuUserCog2,
  LuBookOpen,
  LuCamera,
  LuChevronDown,
  LuX,
  LuBuilding2,
  LuAlertCircle,
  LuClock,
  LuSun,
  LuMoon,
  LuBriefcase,
  LuUser,
  LuPhone,
  LuMessageCircle,
  LuInfo,
} from 'react-icons/lu';
import { FaWeixin } from 'react-icons/fa';
import { z } from 'zod';
import {
  UserOnboardingType,
  AccountSetupSchema,
  AccountSetupType,
  UserInterface,
} from '../../app-schemas/profile.schema';
import content_mgt from '../../api-services/contents/contents.services';
import profile_api from '../../api-services/profile/profile.services';
import { RES_MSSG } from '../../api-services/ConstMssg';
import { SITELINK } from '../../app-routes/Links';
import GlobalLayout from '../../layouts/GlobalLayout';
import LazyLoad from 'react-lazyload';
import { getFirstLetters, getUser, storeUser } from '../../app-utils';
import Modal from '../../components/modal';
import { cn } from '../../components/card';
import TopBar from '../../layouts/~includes/TopBar';
import { useTheme } from '../../context/theme-context';
import SelectDropdown from '../../components/form/SelectDropdown';

// data type
type Location = {
  latitude: number;
  longitude: number;
};

// Time option generator
const generateTimeOptions = () => {
  const times = [];
  for (let hour = 1; hour <= 12; hour++) {
    times.push({ value: `${hour}:00 AM`, label: `${hour}:00 AM` });
    times.push({ value: `${hour}:30 AM`, label: `${hour}:30 AM` });
  }
  for (let hour = 1; hour <= 12; hour++) {
    times.push({ value: `${hour}:00 PM`, label: `${hour}:00 PM` });
    times.push({ value: `${hour}:30 PM`, label: `${hour}:30 PM` });
  }
  return times;
};

const timeOptions = generateTimeOptions();

// custom js fn to handle type Guard
function isLocation(value: any): value is Location {
  return value && typeof value.latitude === 'number' && typeof value.longitude === 'number';
}

// Skeleton Loader Component
const ProfileSkeleton = () => {
  const { isDarkMode } = useTheme();
  return (
    <div className="mt-[48px] animate-pulse">
      <div className={`flex gap-5 items-start justify-start py-3 px-3 pb-3 border-b ${isDarkMode ? 'bg-neutral-800 border-gray-700' : 'bg-white'}`}>
        <div className={`w-[80px] h-[80px] rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
        <div className="mt-2 flex-1">
          <div className={`h-7 w-32 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-2`}></div>
          <div className={`h-4 w-40 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-1`}></div>
          <div className={`h-4 w-48 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
        </div>
      </div>
      <div className={`grid mb-0 p-3 grid-cols-2 gap-4 ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="col-span-full">
            <div className={`h-4 w-24 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-2`}></div>
            <div className={`h-10 w-full rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Minimal Accordion Section Component
const AccordionSection = ({ 
  title, 
  icon: Icon, 
  description,
  isDarkMode, 
  children, 
  isOpen, 
  onToggle,
  badge
}: any) => {
  return (
    <div className="col-span-full">
      {/* Accordion Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full py-3 flex items-start justify-between gap-2 text-left focus:outline-none"
      >
        <div className="flex gap-2 flex-1">
          <Icon className={`text-base mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-medium text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {title}
              </span>
              {badge && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  isDarkMode 
                    ? 'bg-neutral-700 text-gray-300' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {badge}
                </span>
              )}
            </div>
            {description && (
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {description}
              </p>
            )}
          </div>
        </div>
        <div className={`transition-transform duration-200 mt-0.5 ${isOpen ? 'rotate-180' : ''}`}>
          <LuChevronDown className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
        </div>
      </button>

      {/* Accordion Content - Conditionally rendered */}
      {isOpen && (
        <div className="pb-3">
          <div className="grid grid-cols-2 gap-3">
            {children}
          </div>
        </div>
      )}
      
      {/* Minimal divider */}
      <div className={`h-px ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`} />
    </div>
  );
};

// Info Box Component
const OptionalInfoBox = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <div className={`col-span-full mb-3 flex items-center gap-1.5 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
    <LuInfo className="text-custom-500 text-xs" />
    <span>Tap any section to expand and update</span>
  </div>
);

// main-component-ui
const ProfileAccount = () => {
  document.body.classList.remove('overflow-hidden');
  
  const user: UserInterface = getUser();
  const { isDarkMode } = useTheme();

  const [me, setMe] = useState<Partial<UserInterface>>({});
  const [imageModal, setImageModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [countries, setCountries] = useState<any>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isProfilePix, setIsProfilePix] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string>('');

  // Accordion states
  const [openSections, setOpenSections] = useState({
    about: false,
    business: false,
    contact: false,
  });

  // State for time dropdowns
  const [isBusinessOpenOpen, setIsBusinessOpenOpen] = useState(false);
  const [isBusinessCloseOpen, setIsBusinessCloseOpen] = useState(false);

  const AccountSetupDefault: AccountSetupType = {
    first_name: '',
    last_name: '',
    username: '',
    profession: '',
    hobby: '',
    email: '',
    nationality_country_id: -1,
    whatsapp_number: '',
    telegram_number: '',
    wechat_number: '',
    marital_status: '',
    religion: '',
    gender: '',
    bio: '',
    business_name: '',
    business_description: '',
    business_open: '',
    business_close: '',
  };
  
  const [profilePix, setProfilePix] = useState('');
  const [locationAccessMessage, setLocationAccess] = useState<string>('OPEN');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const [isGenderOpen, setIsGenderOpen] = useState(false);
  const [isNationalityOpen, setIsNationalityOpen] = useState(false);

  const maritalStatus = [
    { value: 'Single', label: 'Single' },
    { value: 'Married', label: 'Married' },
    { value: 'Divorced', label: 'Divorced' },
    { value: 'Separated', label: 'Separated' },
    { value: 'Single Parent', label: 'Single Parent' },
  ];

  const religion = [
    { value: 'Christianity', label: 'Christianity' },
    { value: 'Islam', label: 'Islam' },
    { value: 'Buddhism', label: 'Buddhism' },
    { value: 'Judaism', label: 'Judaism' },
    { value: 'Traditionalist', label: 'Traditionalist' },
    { value: 'Atheist', label: 'Atheist' },
  ];

  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Others', label: 'Rather not specify' },
  ];

  const createImage = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (e.target && typeof e.target.result === 'string') {
        const imgElement = document.createElement('img');
        imgElement.src = e.target?.result as string;
        imgElement.style.width = '52px';
        imgElement.style.height = '52px';
        imgElement.style.objectFit = 'cover';
        imgElement.style.borderRadius = '50%';
        imgElement.id = 'imgElement';
        const imageContainer = document.getElementById('imageContainer');
        if (imageContainer) {
          imageContainer.appendChild(imgElement);
        }
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (value: FileList | null) => {
    if (value !== null) {
      setFile(value[0]);
      createImage(value[0]);
      setIsProfilePix(true);
      setSuccessMsg('');
      setErrorMsg('');
      let data: any = {
        profile_picture: value[0],
        longitude: me?.longitude,
        latitude: me?.latitude,
      };
      uploadProfileImage(data);
    }
  };

  const removeImage = () => {
    const imgElement = document.getElementById('imgElement');
    if (imgElement) {
      imgElement.remove();
      setFile(null);
      setIsProfilePix(false);
    }
  };

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    if (successMsg || errorMsg) {
      const timer = setTimeout(() => {
        setSuccessMsg('');
        setErrorMsg('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg, errorMsg]);

  useEffect(() => {
    myProfile();
  }, []);

  const myProfile = async () => {
    setPageLoading(true);
    try {
      const my_profile: any = await profile_api.Profile();
      setMe(my_profile?.data?.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    getCountries();
  }, []);

  // Fixed: Moved the logic directly inside useEffect to avoid dependency issues
  useEffect(() => {
    const loadUserProfile = async () => {
      setLoading(true);
      try {
        const res = await profile_api.Profile();
        if (res?.data?.message === RES_MSSG.SUCCESS) {
          // Update the AccountSetupDefault object
          Object.assign(AccountSetupDefault, {
            ...res.data.data,
            whatsapp_number: res.data.data.whatsapp_number || '',
            telegram_number: res.data.data.telegram_number || '',
            wechat_number: res.data.data.wechat_number || '',
            business_name: res.data.data.business_name || '',
            business_description: res.data.data.business_description || '',
            business_open: res.data.data.business_open || '',
            business_close: res.data.data.business_close || '',
            profession: res.data.data.profession || '',
            hobby: res.data.data.hobby || '',
            marital_status: res.data.data.marital_status || '',
            religion: res.data.data.religion || '',
            bio: res.data.data.bio || '',
          });
          setProfilePix(res.data.data.profile_picture_url);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setErrorMsg('An error occurred while loading your profile!');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array is fine here since AccountSetupDefault is a stable object reference

  const [formData, handleChange, errors, setValidationErrors, handleCustomChange] = useFormInput(
    AccountSetupDefault,
    AccountSetupSchema,
  );

  const getCountries = async () => {
    try {
      const res = await content_mgt.getCountries();
      setCountries(res);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      setLocationAccess('OPEN');

      if ('permissions' in navigator) {
        navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
          if (permissionStatus.state === 'denied') {
            setLocationAccess('DENIED');
          }
        });
      }

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(position.coords);
          },
          (error) => {
            console.error('Geolocation error:', error);
            reject(error);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          },
        );
      } else {
        reject(new Error('Geolocation is not supported by this device.'));
      }
    });
  };

  const handleOnboarding = async (e: FormEvent) => {
    e.preventDefault();
    
    setErrorMsg('');
    setSuccessMsg('');
    
    const PartialAccountSetupSchema = z.object({
      first_name: z.string().min(1, 'Enter a first name'),
      last_name: z.string().min(1, 'Enter a last name'),
      email: z.string().email('Enter a valid email'),
      nationality_country_id: z.number().min(1, 'Select your Nationality'),
      gender: z.string().min(1, 'Please select a gender'),
    });
    
    const validationData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      nationality_country_id: formData.nationality_country_id,
      gender: formData.gender,
    };
    
    const result = PartialAccountSetupSchema.safeParse(validationData);

    if (!result.success) {
      const newErrors: Partial<Record<keyof AccountSetupType, string>> = {};
      result.error.errors.forEach((err) => {
        newErrors[err.path[0] as keyof AccountSetupType] = err.message;
      });
      setValidationErrors(newErrors);
      
      const firstError = document.querySelector('.text-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    try {
      const position = await getUserLocation();
      
      if (isLocation(position)) {
        const coordinates: Location = position;

        const data: any = {
          username: formData.username || undefined,
          first_name: formData.first_name || '',
          last_name: formData.last_name || '',
          nationality_country_id: formData.nationality_country_id || -1,
          longitude: coordinates.longitude,
          latitude: coordinates.latitude,
          email: formData.email || '',
          gender: formData.gender || '',
        };

        if (formData.marital_status && formData.marital_status.trim() !== '') data.marital_status = formData.marital_status;
        if (formData.whatsapp_number && formData.whatsapp_number.trim() !== '') data.whatsapp_number = formData.whatsapp_number;
        if (formData.telegram_number && formData.telegram_number.trim() !== '') data.telegram_number = formData.telegram_number;
        if (formData.wechat_number && formData.wechat_number.trim() !== '') data.wechat_number = formData.wechat_number;
        if (formData.bio && formData.bio.trim() !== '') data.bio = formData.bio;
        if (formData.profession && formData.profession.trim() !== '') data.profession = formData.profession;
        if (formData.hobby && formData.hobby.trim() !== '') data.hobby = formData.hobby;
        if (formData.religion && formData.religion.trim() !== '') data.religion = formData.religion;
        if (formData.business_name && formData.business_name.trim() !== '') data.business_name = formData.business_name;
        if (formData.business_description && formData.business_description.trim() !== '') data.business_description = formData.business_description;
        if (formData.business_open && formData.business_open.trim() !== '') data.business_open = formData.business_open;
        if (formData.business_close && formData.business_close.trim() !== '') data.business_close = formData.business_close;
        
        if (file) data.profile_picture = file;

        await OnbaordUserWithLocation(data);
      } else {
        setErrorMsg('Location is required to proceed. Please enable location services.');
      }
    } catch (err) {
      console.error('Error in handleOnboarding:', err);
      setErrorMsg('Failed to get location. Please check your location settings and try again.');
    }
  };

  const OnbaordUserWithLocation = async (data: UserOnboardingType) => {
    setLoading(true);
    try {
      const res = await profile_api.UpdateProfile(data);

      if (res?.data?.message === RES_MSSG.PROFILE_UPDATED) {
        setSuccessMsg('Profile updated successfully!');
        const profileRes: any = await profile_api.Profile();
        setMe(profileRes?.data?.data);
        let me_local = getUser();
        me_local.profile_picture = profileRes?.data?.data?.profile_picture_url;
        storeUser(me_local);
        
        Object.assign(AccountSetupDefault, profileRes?.data?.data);
      } else {
        setErrorMsg(res?.response?.data?.message || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('Error in OnbaordUserWithLocation:', error);
      setErrorMsg(error?.response?.data?.message || 'An error occurred while updating your profile!');
    } finally {
      setLoading(false);
    }
  };

  const uploadProfileImage = async (data: any) => {
    setPageLoading(true);
    try {
      const res = await profile_api.UpdateProfile(data);
      if (res?.data?.message === RES_MSSG.PROFILE_UPDATED) {
        setSuccessMsg('Profile picture updated successfully!');
        const profileRes: any = await profile_api.Profile();
        setMe(profileRes?.data?.data);
        let me_local = getUser();
        me_local.profile_picture = profileRes?.data?.data?.profile_picture_url;
        storeUser(me_local);
      } else {
        setErrorMsg(res?.response?.data?.message || 'Failed to update profile picture');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setErrorMsg('An error occurred while updating profile picture!');
    } finally {
      setPageLoading(false);
    }
  };

  const getGenderLabel = () => {
    const selected = genderOptions.find(opt => opt.value === formData.gender);
    return selected ? selected.label : 'Select Gender';
  };

  const getNationalityLabel = () => {
    if (!formData.nationality_country_id || formData.nationality_country_id === -1) return 'Select your Nationality';
    const selected = countries?.find((c: any) => c.value === formData.nationality_country_id);
    return selected ? selected.label : 'Select your Nationality';
  };

  const getBusinessOpenLabel = () => {
    if (!formData.business_open) return 'Select opening time';
    return formData.business_open;
  };

  const getBusinessCloseLabel = () => {
    if (!formData.business_close) return 'Select closing time';
    return formData.business_close;
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setIsGenderOpen(false);
      setIsNationalityOpen(false);
      setIsBusinessOpenOpen(false);
      setIsBusinessCloseOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const getFilledOptionalCount = () => {
    let count = 0;
    if (formData.bio && formData.bio.trim() !== '') count++;
    if (formData.profession && formData.profession.trim() !== '') count++;
    if (formData.hobby && formData.hobby.trim() !== '') count++;
    if (formData.marital_status && formData.marital_status.trim() !== '') count++;
    if (formData.religion && formData.religion.trim() !== '') count++;
    return count > 0 ? `${count} filled` : '';
  };

  const getFilledBusinessCount = () => {
    let count = 0;
    if (formData.business_name && formData.business_name.trim() !== '') count++;
    if (formData.business_description && formData.business_description.trim() !== '') count++;
    if (formData.business_open && formData.business_open.trim() !== '') count++;
    if (formData.business_close && formData.business_close.trim() !== '') count++;
    return count > 0 ? `${count} filled` : '';
  };

  const getFilledContactCount = () => {
    let count = 0;
    if (formData.whatsapp_number && formData.whatsapp_number.trim() !== '') count++;
    if (formData.telegram_number && formData.telegram_number.trim() !== '') count++;
    if (formData.wechat_number && formData.wechat_number.trim() !== '') count++;
    return count > 0 ? `${count} filled` : '';
  };

  return (
    <GlobalLayout title="Catapot | My Profile">
      <section className={`min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-[#E1F0F8F7]'}`}>
        <div className="fixed top-0 w-full z-50">
          <TopBar title='Edit Profile' link={SITELINK.HOME} />
        </div>

        {pageLoading ? (
          <ProfileSkeleton />
        ) : (
          <div className="mt-[48px] pb-0">
            {/* Profile Header Section */}
            <div className={`flex gap-5 items-start justify-start py-3 px-3 border-b ${isDarkMode ? 'bg-neutral-800 border-gray-700' : 'bg-white'}`}>
              <div className="overflow-hidden flex justify-center">
                <div className="relative">
                  {me?.profile_picture_url ? (
                    <LazyLoad height={300} offset={1000} once>
                      <img
                        alt="me"
                        src={me?.profile_picture_url}
                        className="border-[1px] object-cover rounded-full w-[80px] h-[80px] bg-gray-200 dark:bg-gray-700 shadow-sm overflow-hidden"
                      />
                    </LazyLoad>
                  ) : (
                    <span className="flex items-center justify-center w-[80px] h-[80px] bg-[#223f64] rounded-full border border-green-100 dark:border-green-800">
                      <h3 className="text-white text-[3rem] uppercase font-[600]">
                        {getFirstLetters(me?.first_name ?? user?.first_name ?? 'C')}
                      </h3>
                    </span>
                  )}
                  <button className={`text-gray-500 text-lg rounded-full absolute bottom-1 right-[0px] p-1.5 ${isDarkMode ? 'bg-neutral-700 border border-gray-600' : 'bg-gray-50 border shadow-sm'}`}>
                    <label htmlFor="dropzone-file" className="text-[13px] cursor-pointer">
                      <LuCamera className={isDarkMode ? 'text-gray-300' : ''} />
                      <input
                        id="dropzone-file"
                        ref={fileInputRef}
                        type="file"
                        accept=".png, .jpeg, .jpg"
                        onChange={(e) => handleFileChange(e.currentTarget.files)}
                        className="hidden"
                      />
                    </label>
                  </button>
                </div>
              </div>

              <div className="mt-2 flex-1">
                <h3 className={`font-semibold text-2xl mb-0 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {me?.username ?? user?.username}
                </h3>
                <p className={`text-xs text-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {me?.first_name} {me?.last_name}
                </p>
                <p className={`text-xs text-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {me?.email}
                </p>
              </div>
            </div>

            {/* Success/Error Messages */}
            {successMsg && (
              <div className="mx-3 mt-3">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md py-2 px-3">
                  <p className={`text-sm ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                    {successMsg}
                  </p>
                </div>
              </div>
            )}

            {errorMsg && (
              <div className="mx-3 mt-3">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md py-2 px-3 flex items-start gap-2">
                  <LuAlertCircle className="text-red-500 mt-0.5 flex-shrink-0" />
                  <p className={`text-sm flex-1 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                    {errorMsg}
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleOnboarding} className={`p-3 ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
              <OptionalInfoBox isDarkMode={isDarkMode} />

              {/* REQUIRED PERSONAL INFORMATION - Always visible */}
              <div className="mb-4">
                <div className="flex items-start gap-2 mb-2">
                  <LuUser className={`text-base mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                  <div>
                    <h3 className={`font-medium text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      Required Information
                    </h3>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Please fill in these fields to complete your profile
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <InputField
                    className="w-full text-sm rounded-lg"
                    type="text"
                    id="first_name"
                    placeholder="First Name"
                    label="First Name"
                    value={formData.first_name || ''}
                    onChange={handleChange}
                    required
                    icon={LuUser2}
                    error={errors?.first_name}
                    darkMode={isDarkMode}
                  />

                  <InputField
                    className="w-full text-sm rounded-lg"
                    type="text"
                    id="last_name"
                    placeholder="Last Name"
                    label="Last Name"
                    value={formData.last_name || ''}
                    onChange={handleChange}
                    required
                    icon={LuUser2}
                    error={errors?.last_name}
                    darkMode={isDarkMode}
                  />

                  {/* Gender Dropdown */}
                  <div className="relative">
                    <label className={`flex text-xs font-[600] mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Gender <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="relative">
                      <span className={`absolute p-[5px] rounded-[50px] top-[8px] left-[7px] text-base z-10 ${isDarkMode ? 'text-gray-400' : 'text-[#98A1B0]'}`}>
                        <LuUser2 />
                      </span>
                      <div
                        className={`w-full text-sm rounded-lg border rounded-[3px] pl-10 px-4 py-3 cursor-pointer flex justify-between items-center ${isDarkMode ? 'bg-neutral-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsGenderOpen(!isGenderOpen);
                          setIsNationalityOpen(false);
                        }}
                      >
                        <span className={formData.gender ? (isDarkMode ? 'text-white' : 'text-gray-700') : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}>
                          {getGenderLabel()}
                        </span>
                        <LuChevronDown className={`text-gray-500 transition-transform text-xs ${isGenderOpen ? 'rotate-180' : ''}`} />
                      </div>
                      
                      {isGenderOpen && (
                        <div className={`absolute z-50 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-auto ${isDarkMode ? 'bg-neutral-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                          {genderOptions.map((option) => (
                            <div
                              key={option.value}
                              className={cn(
                                "px-4 py-2 cursor-pointer text-sm",
                                option.value === formData.gender && (isDarkMode ? 'bg-neutral-700' : 'bg-blue-50'),
                                isDarkMode ? 'text-gray-200 hover:bg-neutral-700' : 'hover:bg-gray-50'
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCustomChange('gender', option.value);
                                setIsGenderOpen(false);
                              }}
                            >
                              {option.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {errors?.gender && (
                      <p className="text-red-500 text-xs mt-1">{errors.gender}</p>
                    )}
                  </div>

                  {/* Nationality Dropdown */}
                  <div className="relative">
                    <label className={`flex text-xs font-[600] mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Nationality <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="relative">
                      <span className={`absolute p-[5px] rounded-[50px] top-[8px] left-[7px] text-base z-10 ${isDarkMode ? 'text-gray-400' : 'text-[#98A1B0]'}`}>
                        <LuGlobe />
                      </span>
                      <div
                        className={`w-full text-sm rounded-lg border rounded-[3px] pl-10 px-4 py-3 cursor-pointer flex justify-between items-center ${isDarkMode ? 'bg-neutral-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsNationalityOpen(!isNationalityOpen);
                          setIsGenderOpen(false);
                        }}
                      >
                        <span className={formData.nationality_country_id && formData.nationality_country_id !== -1 ? (isDarkMode ? 'text-white' : 'text-gray-700') : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}>
                          {getNationalityLabel()}
                        </span>
                        <LuChevronDown className={`text-gray-500 transition-transform text-xs ${isNationalityOpen ? 'rotate-180' : ''}`} />
                      </div>
                      
                      {isNationalityOpen && (
                        <div className={`absolute z-50 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-auto ${isDarkMode ? 'bg-neutral-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                          {countries?.length > 0 ? (
                            countries.map((option: any) => (
                              <div
                                key={option.value}
                                className={cn(
                                  "px-4 py-2 cursor-pointer text-sm",
                                  option.value === formData.nationality_country_id && (isDarkMode ? 'bg-neutral-700' : 'bg-blue-50'),
                                  isDarkMode ? 'text-gray-200 hover:bg-neutral-700' : 'hover:bg-gray-50'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCustomChange('nationality_country_id', option.value);
                                  setIsNationalityOpen(false);
                                }}
                              >
                                {option.label}
                              </div>
                            ))
                          ) : (
                            <div className={`px-4 py-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Loading countries...
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {errors?.nationality_country_id && (
                      <p className="text-red-500 text-xs mt-1">{errors.nationality_country_id}</p>
                    )}
                  </div>

                  {/* Hidden fields */}
                  <InputField
                    className="hidden"
                    type="email"
                    id="email"
                    value={formData.email || ''}
                    onChange={handleChange}
                    darkMode={isDarkMode}
                  />
                </div>
              </div>

              <div className={`h-px ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} my-2`} />

              {/* ABOUT YOU - Accordion */}
              <AccordionSection
                title="About You"
                icon={LuBookOpen}
                description="Tell others about yourself, your interests, and what makes you unique"
                isDarkMode={isDarkMode}
                isOpen={openSections.about}
                onToggle={() => toggleSection('about')}
                badge={getFilledOptionalCount()}
              >
                <TextArea
                  className="col-span-full text-sm rounded-lg"
                  id="bio"
                  placeholder="Tell us about yourself..."
                  label="Bio"
                  icon={LuBookOpen}
                  value={formData.bio || ''}
                  onChange={handleChange}
                  error={errors?.bio}
                  darkMode={isDarkMode}
                />

                <InputField
                  className="col-span-full text-sm rounded-lg"
                  type="text"
                  id="profession"
                  placeholder="Your profession"
                  label="Profession"
                  value={formData.profession || ''}
                  onChange={handleChange}
                  icon={LuUserCog2}
                  error={errors?.profession}
                  darkMode={isDarkMode}
                />

                <InputField
                  className="col-span-full text-sm rounded-lg"
                  type="text"
                  id="hobby"
                  placeholder="Your hobbies"
                  label="Hobbies"
                  value={formData.hobby || ''}
                  onChange={handleChange}
                  icon={LuUserCog2}
                  error={errors?.hobby}
                  darkMode={isDarkMode}
                />

                <SelectDropdown
                  className="col-span-full text-sm rounded-lg"
                  id="marital_status"
                  placeholder="Select status"
                  label="Marital Status"
                  value={formData.marital_status || ''}
                  onChange={(e) => handleCustomChange('marital_status', e)}
                  options={maritalStatus}
                  icon={LuUsers}
                  error={errors?.marital_status}
                  darkMode={isDarkMode}
                />

                <SelectDropdown
                  className="col-span-full text-sm rounded-lg"
                  id="religion"
                  placeholder="Select religion"
                  label="Religion"
                  value={formData.religion || ''}
                  onChange={(e) => handleCustomChange('religion', e)}
                  options={religion}
                  icon={LuChurch}
                  error={errors?.religion}
                  darkMode={isDarkMode}
                />
              </AccordionSection>

              {/* BUSINESS - Accordion */}
              <AccordionSection
                title="Business or Services"
                icon={LuBriefcase}
                description="If you run a business or offer services, share the details here"
                isDarkMode={isDarkMode}
                isOpen={openSections.business}
                onToggle={() => toggleSection('business')}
                badge={getFilledBusinessCount()}
              >
                <InputField
                  className="col-span-full text-sm rounded-lg"
                  type="text"
                  id="business_name"
                  placeholder="Business name"
                  label="Business Name"
                  value={formData.business_name || ''}
                  onChange={handleChange}
                  icon={LuBuilding2}
                  error={errors?.business_name}
                  darkMode={isDarkMode}
                />

                <TextArea
                  className="col-span-full text-sm rounded-lg"
                  id="business_description"
                  placeholder="Describe your business..."
                  label="Business Description"
                  icon={LuBuilding2}
                  value={formData.business_description || ''}
                  onChange={handleChange}
                  error={errors?.business_description}
                  darkMode={isDarkMode}
                />

                <div className="col-span-full">
                  <div className="flex flex-col gap-3">
                    <div className="relative w-full">
                      <label className={`flex text-xs font-[600] mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <LuSun className="mr-1 text-xs" /> Opening Time
                      </label>
                      <div className="relative">
                        <span className={`absolute p-[5px] rounded-[50px] top-[8px] left-[7px] text-base z-10 ${isDarkMode ? 'text-gray-400' : 'text-[#98A1B0]'}`}>
                          <LuClock className="text-xs" />
                        </span>
                        <div
                          className={`w-full text-sm rounded-lg border rounded-[3px] pl-10 px-4 py-3 cursor-pointer flex justify-between items-center ${isDarkMode ? 'bg-neutral-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsBusinessOpenOpen(!isBusinessOpenOpen);
                            setIsBusinessCloseOpen(false);
                          }}
                        >
                          <span className={formData.business_open ? (isDarkMode ? 'text-white' : 'text-gray-700') : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}>
                            {getBusinessOpenLabel()}
                          </span>
                          <LuChevronDown className={`text-gray-500 transition-transform text-xs ${isBusinessOpenOpen ? 'rotate-180' : ''}`} />
                        </div>
                        
                        {isBusinessOpenOpen && (
                          <div className={`absolute z-50 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-auto ${isDarkMode ? 'bg-neutral-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <div
                              className={cn(
                                "px-4 py-2 cursor-pointer text-sm",
                                !formData.business_open && (isDarkMode ? 'bg-neutral-700' : 'bg-gray-50'),
                                isDarkMode ? 'text-gray-200 hover:bg-neutral-700' : 'hover:bg-gray-50'
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCustomChange('business_open', '');
                                setIsBusinessOpenOpen(false);
                              }}
                            >
                              Not specified
                            </div>
                            {timeOptions.map((option) => (
                              <div
                                key={option.value}
                                className={cn(
                                  "px-4 py-2 cursor-pointer text-sm",
                                  option.value === formData.business_open && (isDarkMode ? 'bg-neutral-700' : 'bg-blue-50'),
                                  isDarkMode ? 'text-gray-200 hover:bg-neutral-700' : 'hover:bg-gray-50'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCustomChange('business_open', option.value);
                                  setIsBusinessOpenOpen(false);
                                }}
                              >
                                {option.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="relative w-full">
                      <label className={`flex text-xs font-[600] mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <LuMoon className="mr-1 text-xs" /> Closing Time
                      </label>
                      <div className="relative">
                        <span className={`absolute p-[5px] rounded-[50px] top-[8px] left-[7px] text-base z-10 ${isDarkMode ? 'text-gray-400' : 'text-[#98A1B0]'}`}>
                          <LuClock className="text-xs" />
                        </span>
                        <div
                          className={`w-full text-sm rounded-lg border rounded-[3px] pl-10 px-4 py-3 cursor-pointer flex justify-between items-center ${isDarkMode ? 'bg-neutral-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsBusinessCloseOpen(!isBusinessCloseOpen);
                            setIsBusinessOpenOpen(false);
                          }}
                        >
                          <span className={formData.business_close ? (isDarkMode ? 'text-white' : 'text-gray-700') : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}>
                            {getBusinessCloseLabel()}
                          </span>
                          <LuChevronDown className={`text-gray-500 transition-transform text-xs ${isBusinessCloseOpen ? 'rotate-180' : ''}`} />
                        </div>
                        
                        {isBusinessCloseOpen && (
                          <div className={`absolute z-50 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-auto ${isDarkMode ? 'bg-neutral-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <div
                              className={cn(
                                "px-4 py-2 cursor-pointer text-sm",
                                !formData.business_close && (isDarkMode ? 'bg-neutral-700' : 'bg-gray-50'),
                                isDarkMode ? 'text-gray-200 hover:bg-neutral-700' : 'hover:bg-gray-50'
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCustomChange('business_close', '');
                                setIsBusinessCloseOpen(false);
                              }}
                            >
                              Not specified
                            </div>
                            {timeOptions.map((option) => (
                              <div
                                key={option.value}
                                className={cn(
                                  "px-4 py-2 cursor-pointer text-sm",
                                  option.value === formData.business_close && (isDarkMode ? 'bg-neutral-700' : 'bg-blue-50'),
                                  isDarkMode ? 'text-gray-200 hover:bg-neutral-700' : 'hover:bg-gray-50'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCustomChange('business_close', option.value);
                                  setIsBusinessCloseOpen(false);
                                }}
                              >
                                {option.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionSection>

              {/* CONTACT - Accordion */}
              <AccordionSection
                title="Contact Information"
                icon={LuPhone}
                description="Share your contact details for networking and collaboration"
                isDarkMode={isDarkMode}
                isOpen={openSections.contact}
                onToggle={() => toggleSection('contact')}
                badge={getFilledContactCount()}
              >
                <InputField
                  className="col-span-full text-sm rounded-lg"
                  type="tel"
                  id="whatsapp_number"
                  placeholder="WhatsApp number"
                  label="WhatsApp"
                  value={formData.whatsapp_number || ''}
                  onChange={handleChange}
                  icon={LuMessageCircle}
                  error={errors?.whatsapp_number}
                  darkMode={isDarkMode}
                />

                <InputField
                  className="col-span-full text-sm rounded-lg"
                  type="tel"
                  id="telegram_number"
                  placeholder="Telegram username"
                  label="Telegram"
                  value={formData.telegram_number || ''}
                  onChange={handleChange}
                  icon={LuMessageCircle}
                  error={errors?.telegram_number}
                  darkMode={isDarkMode}
                />

                <InputField
                  className="col-span-full text-sm rounded-lg"
                  type="text"
                  id="wechat_number"
                  placeholder="WeChat ID"
                  label="WeChat"
                  value={formData.wechat_number || ''}
                  onChange={handleChange}
                  icon={FaWeixin}
                  error={errors?.wechat_number}
                  darkMode={isDarkMode}
                />
              </AccordionSection>

              {/* Hidden fields */}
              <InputField
                className="hidden"
                type="text"
                id="username"
                value={formData.username || ''}
                onChange={handleChange}
                darkMode={isDarkMode}
              />

              {/* Location Access Warning */}
              {locationAccessMessage === 'DENIED' && (
                <div className="mt-4">
                  <div className="text-[12px] flex bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md py-2 px-3">
                    <div className="flex-1">
                      <p className={`text-xs font-medium ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                        Location access required
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Please enable location services to update your profile
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="mt-6">
                <ButtonComponent
                  className="w-full text-sm py-3 rounded-lg text-white bg-custom-600 hover:bg-custom-700 transition-colors"
                  type="submit"
                  disabled={loading}
                  loading={loading}
                >
                  {loading ? 'Updating...' : 'Update Profile'}
                </ButtonComponent>
              </div>
            </form>
          </div>
        )}
      </section>

      <Modal isOpen={imageModal} onClose={() => setImageModal(!imageModal)}>
        <section className={`p-3 w-[70vw] ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
          <div className="flex justify-end">
            <button
              className="text-red-600 font-medium text-xs justify-end flex p-3 hover:text-red-700 transition-colors"
              onClick={() => setImageModal(!imageModal)}
            >
              <LuX className="size-4" />
            </button>
          </div>
          <p className={`text-center text-semibold text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Upload image
          </p>
          <div className={`p-24 border border-dotted rounded-lg ${isDarkMode ? 'border-gray-600 bg-neutral-700' : 'border-gray-400 bg-gray-50'}`}>
            <div className="flex gap-2 items-center">
              {isProfilePix ? (
                <span
                  className="text-red-600 text-[13px] font-medium cursor-pointer hover:text-red-700 transition-colors"
                  onClick={removeImage}
                >
                  Remove Picture
                </span>
              ) : (
                <label htmlFor="dropzone-file-modal" className="text-[13px] flex py-1 gap-1 cursor-pointer">
                  <LuImage className="text-lg text-custom-600" />
                  <div id="imgElement"></div>
                  <span className="text-custom-600 font-medium">
                    {profilePix !== '' && !isProfilePix ? 'Change Profile Picture' : 'Add Profile Picture'}
                  </span>
                  <input
                    id="dropzone-file-modal"
                    ref={fileInputRef}
                    type="file"
                    accept=".png, .jpeg, .jpg"
                    onChange={(e) => handleFileChange(e.currentTarget.files)}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>
        </section>
      </Modal>
    </GlobalLayout>
  );
};

export default ProfileAccount;